import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { L } from "@/lib/i18n";

/**
 * AmbientAudio — Phase 3.5 background music + Phase 4 per-scene override.
 *
 * Behaviour:
 *   - Renders TWO hidden <audio loop> elements (A/B) so we can crossfade
 *     between the project ambient and a per-scene override over ~1.2s.
 *   - Starts without audio sources. Music downloads only after unmuting,
 *     or when restoring the user's saved unmuted preference.
 *   - Per-slug "unmuted" preference persists in localStorage.
 *   - Keeps configured volume stable across scenes using the same track.
 *
 * Props:
 *   - url            project.background_audio_url (fallback layer)
 *   - volume         project.background_audio_volume (0..1)
 *   - slug           project slug (localStorage key)
 *   - sceneType      active scene type — drives auto-duck
 *   - sceneAudioUrl  active scene's background_audio_url override (Phase 4)
 *   - sceneAudioVolume  active scene's background_audio_volume override
 */
const LS_KEY = (slug) => `evb.ambient.unmuted.${slug}`;
const FADE_MS = 1200;

export default function AmbientAudio({ lang = "en",
  url, volume = 0.35, slug,
  sceneAudioUrl = null, sceneAudioVolume = null,
}) {
  // A = currently audible layer, B = staged for incoming track during fade.
  const aRef = useRef(null);
  const bRef = useRef(null);
  const fadeTimer = useRef(null);
  const hadAudioSource = useRef(false);
  const [activeLayer, setActiveLayer] = useState("A"); // which layer holds the *current* track
  const [aSrc, setASrc] = useState(sceneAudioUrl || url || "");
  const [bSrc, setBSrc] = useState("");
  const [unmuted, setUnmuted] = useState(false);

  // The track that *should* be playing right now (scene override > project).
  // Volume is a constant — no per-scene-type ducking; scene changes that
  // keep the same `desiredUrl` must not touch volume or playback state.
  const desiredUrl = sceneAudioUrl || url || "";
  const desiredVolume = sceneAudioUrl
    ? (sceneAudioVolume != null ? sceneAudioVolume : 0.5)
    : volume;

  // Recover previous unmute preference per project
  useEffect(() => {
    if (!slug) return;
    try {
      setUnmuted(localStorage.getItem(LS_KEY(slug)) === "1");
    } catch (_) { /* ignore */ }
  }, [slug]);

  // Crossfade when desiredUrl changes.
  useEffect(() => {
    if (!unmuted) {
      // Keep the next selected track current without attaching any source
      // to a media element while silent (including scene overrides).
      setASrc(desiredUrl);
      setBSrc("");
      setActiveLayer("A");
      return undefined;
    }
    const currentRef = activeLayer === "A" ? aRef : bRef;
    const nextRef = activeLayer === "A" ? bRef : aRef;
    const currentSrc = activeLayer === "A" ? aSrc : bSrc;
    if (desiredUrl === currentSrc) return; // nothing to do
    if (!desiredUrl) {
      // Fade out current layer to silent.
      return fadeAudio(currentRef.current, currentRef.current?.volume ?? 0, 0, FADE_MS);
    }
    // Stage the new track in the inactive layer and crossfade.
    if (activeLayer === "A") setBSrc(desiredUrl); else setASrc(desiredUrl);
    // Wait a tick for the src to mount, then play + fade.
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
    let finishTimer;
    let cancelOutgoing = () => {};
    let cancelIncoming = () => {};
    fadeTimer.current = setTimeout(() => {
      const a = currentRef.current;
      const b = nextRef.current;
      if (!b) return;
      try {
        b.muted = !unmuted;
        b.loop = true;
        b.volume = 0;
        const p = b.play();
        if (p && p.catch) p.catch(() => { /* iOS gesture */ });
      } catch (_) { /* ignore */ }
      const targetVol = clampVol(desiredVolume);
      cancelOutgoing = fadeAudio(a, a?.volume ?? 0, 0, FADE_MS);
      cancelIncoming = fadeAudio(b, 0, targetVol, FADE_MS);
      // Flip active layer after the fade.
      finishTimer = setTimeout(() => {
        setActiveLayer(activeLayer === "A" ? "B" : "A");
        if (activeLayer === "A") setASrc(""); else setBSrc("");
        try { if (a) { a.pause(); a.removeAttribute("src"); a.load(); } } catch (_) { /* ignore */ }
      }, FADE_MS);
    }, 30);
    return () => {
      clearTimeout(fadeTimer.current);
      clearTimeout(finishTimer);
      cancelOutgoing();
      cancelIncoming();
    };
  }, [desiredUrl, unmuted]);

  // Sync mute + volume on the *active* layer ONLY when the user toggles
  // mute or the configured volume actually changes. Scene changes that
  // don't change the URL must leave volume untouched — preventing the
  // "music dipping up and down on every scene" symptom.
  useEffect(() => {
    const layers = [aRef.current, bRef.current].filter(Boolean);
    layers.forEach((el) => { el.muted = !unmuted; el.loop = true; });
    if (!unmuted) {
      if (hadAudioSource.current) {
        layers.forEach(el => { try { el.pause(); el.removeAttribute("src"); el.load(); } catch (_) { /* ignore */ } });
        hadAudioSource.current = false;
      }
      return undefined;
    }
    hadAudioSource.current = true;
    const active = activeLayer === "A" ? aRef.current : bRef.current;
    if (!active || !desiredUrl) return;
    const targetVol = clampVol(desiredVolume);
    // Smooth ramp so an admin volume change doesn't pop.
    const cancelFade = fadeAudio(active, active.volume ?? targetVol, targetVol, 300);
    // Resume playback iff it isn't already playing. Calling play() on an
    // already-playing element is a no-op per spec, but we guard anyway so
    // unrelated re-renders never re-seek the track to 0.
    if (active.paused) {
      const p = active.play();
      if (p && p.catch) p.catch(() => { /* iOS — muted always succeeds */ });
    }
    return cancelFade;
  }, [unmuted, desiredVolume]);

  useEffect(() => {
    const layers = [aRef.current, bRef.current];
    return () => layers.forEach(el => {
      if (!el?.getAttribute("src")) return;
      try { el.pause(); el.removeAttribute("src"); el.load(); } catch (_) { /* ignore */ }
    });
  }, []);

  if (!url && !sceneAudioUrl) return null;

  return (
    <>
      <audio ref={aRef} src={unmuted ? (aSrc || undefined) : undefined} preload={unmuted ? "auto" : "none"} data-testid="ambient-audio" />
      <audio ref={bRef} src={unmuted ? (bSrc || undefined) : undefined} preload={unmuted ? "auto" : "none"} data-testid="ambient-audio-secondary" />
      <button
        type="button"
        onClick={() => {
          const next = !unmuted;
          setUnmuted(next);
          try {
            if (next) localStorage.setItem(LS_KEY(slug), "1");
            else localStorage.removeItem(LS_KEY(slug));
          } catch (_) { /* ignore */ }
        }}
        className="pill-nav inline-flex items-center justify-center text-white text-xs tracking-wider uppercase px-3 py-2 rounded-full hover:bg-white/10 transition"
        title={L(lang, unmuted ? "mute_music" : "play_music")}
        aria-label={L(lang, unmuted ? "mute_background_music" : "play_background_music")}
        data-testid="ambient-audio-toggle"
      >
        {unmuted ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
      </button>
    </>
  );
}

function clampVol(v) {
  return Math.max(0, Math.min(1, Number(v) || 0));
}

function fadeAudio(el, from, to, ms) {
  if (!el) return () => {};
  const start = performance.now();
  let frame;
  const tick = (t) => {
    const k = Math.min(1, (t - start) / ms);
    try { el.volume = Math.max(0, Math.min(1, from + (to - from) * k)); }
    catch (_) { /* ignore */ }
    if (k < 1) frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(frame);
}
